// Turn picked or dropped files, folders and .zip files into one flat list of photos.
// Ledger: docs/ledgers/venue-editing/VENUE-EDITING-LEDGER.md, task V1b.
//
// A .zip file is never read whole. Only its table of contents (the "central
// directory" at the end of every zip) is read here. Each photo is unpacked later,
// one at a time, when its turn in the upload queue comes (openSource).

import { checkFile, isJunkPath, MAX_FILE_BYTES, MAX_SCAN, pathOf, readBytes } from './photoUpload';

export const ZIP_UNREADABLE = 'This .zip file could not be opened. Unzip it, then add the photos.';
export const ZIP_PASSWORD = 'This .zip file has a password. Unzip it, then add the photos.';
export const NO_PHOTOS_ZIP = 'No photos found in this .zip file.';
export const NO_PHOTOS_FOLDER = 'No photos found in this folder.';
export const tooManyMessage = (what) => `Stopped after ${MAX_SCAN.toLocaleString('en-US')} files. Pick a smaller ${what}.`;

export const isZipFile = (file) => /\.zip$/i.test(file.name || '') || /^application\/(x-)?zip(-compressed)?$/i.test(file.type || '');

class ZipError extends Error {}

const u16 = (b, i) => b[i] | (b[i + 1] << 8);
const u32 = (b, i) => (b[i] | (b[i + 1] << 8) | (b[i + 2] << 16) | (b[i + 3] << 24)) >>> 0;
const END_SIGNATURE = 0x06054b50;
const ENTRY_SIGNATURE = 0x02014b50;
const LOCAL_SIGNATURE = 0x04034b50;
// A real table of contents for 5,000 files is under 1 MB.
const MAX_DIRECTORY_BYTES = 16 * 1024 * 1024;
const S_IFMT = 0o170000;
const S_IFLNK = 0o120000;

// Unpack "deflate" data with the browser's own tool. Stops past `limit` bytes,
// even if the zip claims a smaller size (zip bomb).
export const inflateRaw = async (data, limit) => {
  const stream = new DecompressionStream('deflate-raw');
  const writer = stream.writable.getWriter();
  writer.write(data).catch(() => {});
  writer.close().catch(() => {});
  const reader = stream.readable.getReader();
  const parts = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return parts;
    total += value.length;
    if (total > limit) {
      reader.cancel().catch(() => {});
      throw new ZipError('too large');
    }
    parts.push(value);
  }
};

const openEntry = async (zip, entry) => {
  const head = await readBytes(zip, entry.offset, entry.offset + 30);
  if (head.length < 30 || u32(head, 0) !== LOCAL_SIGNATURE) throw new ZipError('bad entry');
  const start = entry.offset + 30 + u16(head, 26) + u16(head, 28);
  const end = start + entry.compressedSize;
  if (end > zip.size || entry.size > MAX_FILE_BYTES || entry.compressedSize > MAX_FILE_BYTES) throw new ZipError('bad entry');
  if (entry.method === 0) {
    if (entry.compressedSize !== entry.size) throw new ZipError('bad entry');
    return new File([zip.slice(start, end)], entry.name);
  }
  if (entry.method !== 8) throw new ZipError('unsupported method');
  return new File(await inflateRaw(await readBytes(zip, start, end), MAX_FILE_BYTES), entry.name);
};

// Read the zip's table of contents. Returns { sources, truncated }.
// Throws ZipError for a damaged, unsupported (ZIP64) or password zip.
export const readZip = async (zip) => {
  const tailStart = Math.max(0, zip.size - (22 + 0xffff));
  const tail = await readBytes(zip, tailStart, zip.size);
  let end = -1;
  for (let i = tail.length - 22; i >= 0; i -= 1) {
    if (u32(tail, i) === END_SIGNATURE) {
      end = i;
      break;
    }
  }
  if (end < 0) throw new ZipError('no end record');
  const count = u16(tail, end + 10);
  const directorySize = u32(tail, end + 12);
  const directoryStart = u32(tail, end + 16);
  if (directoryStart + directorySize > tailStart + end || directorySize > MAX_DIRECTORY_BYTES) {
    throw new ZipError('bad directory'); // also ZIP64 (over 4 GB), whose values are 0xFFFFFFFF
  }
  const dir = await readBytes(zip, directoryStart, directoryStart + directorySize);

  const utf8 = new TextDecoder();
  const sources = [];
  let truncated = false;
  let password = false;
  let at = 0;
  for (let n = 0; n < count; n += 1) {
    if (at + 46 > dir.length || u32(dir, at) !== ENTRY_SIGNATURE) throw new ZipError('bad directory');
    const nameLength = u16(dir, at + 28);
    const next = at + 46 + nameLength + u16(dir, at + 30) + u16(dir, at + 32);
    if (next > dir.length) throw new ZipError('bad directory');
    const path = utf8.decode(dir.subarray(at + 46, at + 46 + nameLength)).replace(/\\/g, '/');
    const isFolder = path.endsWith('/');
    const isLink = ((u32(dir, at + 38) >>> 16) & S_IFMT) === S_IFLNK;
    if (!isFolder && !isLink && !isJunkPath(path)) {
      if (sources.length >= MAX_SCAN) {
        truncated = true;
        break;
      }
      if (u16(dir, at + 8) & 1) password = true;
      const entry = {
        name: path.split('/').pop(),
        method: u16(dir, at + 10),
        compressedSize: u32(dir, at + 20),
        size: u32(dir, at + 24),
        offset: u32(dir, at + 42)
      };
      sources.push({
        name: entry.name,
        relativePath: path,
        type: '',
        size: Math.max(entry.size, entry.compressedSize),
        open: () => openEntry(zip, entry)
      });
    }
    at = next;
  }
  if (password) throw new ZipError('password');
  return { sources, truncated };
};

const hasPhoto = (files) => files.some((file) => !isJunkPath(pathOf(file)) && !checkFile(file));

// Picked or dropped files → { sources, notes }. Each .zip file is replaced by
// the photos inside it. `folder`: the files came from a folder.
export const collectSources = async (files, { folder = false, truncated = false } = {}) => {
  const sources = [];
  const notes = [];
  let list = [...files];
  if (folder && list.length > MAX_SCAN) {
    list = list.slice(0, MAX_SCAN);
    truncated = true;
  }
  const loose = [];
  for (const file of list) {
    if (!isZipFile(file) || folder) {
      loose.push(file);
      continue;
    }
    try {
      const zip = await readZip(file);
      if (zip.truncated) notes.push(tooManyMessage('.zip file'));
      if (!hasPhoto(zip.sources)) notes.push(NO_PHOTOS_ZIP);
      sources.push(...zip.sources);
    } catch (e) {
      notes.push(e instanceof ZipError && e.message === 'password' ? ZIP_PASSWORD : ZIP_UNREADABLE);
    }
  }
  if (truncated) notes.push(tooManyMessage('folder'));
  if (folder && !hasPhoto(loose)) notes.push(NO_PHOTOS_FOLDER);
  sources.push(...loose);
  return { sources, notes: [...new Set(notes)] };
};

// --- Dropped folders ---

// Must run during the drop event: the browser forgets the items after it.
export const entriesFromDrop = (dataTransfer) =>
  [...((dataTransfer && dataTransfer.items) || [])].map((item) => item.kind === 'file' && item.webkitGetAsEntry && item.webkitGetAsEntry()).filter(Boolean);

// Walk dropped files and folders (and sub-folders). Returns { files, truncated }.
export const readDroppedEntries = async (entries) => {
  const files = [];
  let truncated = false;
  const walk = async (entry) => {
    if (truncated || isJunkPath(entry.name)) return;
    if (entry.isFile) {
      if (files.length >= MAX_SCAN) {
        truncated = true;
        return;
      }
      const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
      Object.defineProperty(file, 'relativePath', { value: entry.fullPath.replace(/^\//, '') });
      files.push(file);
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      // Chrome gives at most 100 entries per call: read until it gives none.
      for (;;) {
        const batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
        if (!batch.length || truncated) return;
        for (const child of batch) await walk(child);
      }
    }
  };
  for (const entry of entries) await walk(entry);
  return { files, truncated };
};
